import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_ORDER,
  CUSTOMER_ORDER_SUMMARY,
  CustomerOrder,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  removeEmptyFilters,
  Toggle,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSubscription,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  wsStitchWith,
  type Column,
  type CompositePipeFunction,
  type ICustomerOrderSummary,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';

import { useSpaceParameterOpenClause } from '@src/components/AppLayout/SpaceParameters';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import type { WebsocketRequest } from '@talos/kyoko';
import type { GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { pipe } from 'rxjs';
import { useFeatureFlag } from '../../../hooks';
import { useBlotterState } from '../../../providers/BlottersContext';
import { useCustomerOrdersColumns } from './useCustomerOrdersColumns';
import {
  colIDToFilterBuilderKey,
  CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT,
  onlyServerFilterKeys,
  useCustomerOrdersFilter,
  type CustomerOrdersTableFilter,
} from './useCustomerOrdersFilter';
import { useCustomerOrdersMenu } from './useCustomerOrdersMenu';

export interface FilteredCustomerOrderParams {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: (keyof CustomerOrder | Partial<Column>)[];
  defaultFilter?: CustomerOrdersTableFilter;
  initialIsOpen?: boolean;
  layoutId?: string;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<CustomerOrder>) => void;
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
}

export function CustomerOrdersBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  layoutId,
  onCloneTab,
  onRowDoubleClicked,
  generateOrderDetailsRoute,
}: FilteredCustomerOrderParams) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const { enableMonitoringCustomerOrderSummaryColumns, setEnableMonitoringCustomerOrderSummaryColumns } =
    useBlotterState();
  const columns = useCustomerOrdersColumns({ defaultColumns, enableMonitoringCustomerOrderSummaryColumns });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = useCustomerOrdersFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const openClause = useSpaceParameterOpenClause(
    filterBuilderAccordion.openClause,
    CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT
  );

  const CustomerOrdersMenu = useCustomerOrdersMenu({
    openClause,
    filterableProperties: filterBuilderProps.properties,
    generateOrderDetailsRoute,
    layoutId,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(
    () => [...columns, ...CustomerOrdersMenu.columns],
    [CustomerOrdersMenu.columns, columns]
  );

  const { showBlotterPauseButton } = useFeatureFlag();

  /**
   * We need to keep the customerOrder sub and the customerOrderSummary sub aligned, we do this since they
   * should always be requesting the same data and support the same filters.
   */
  const [customerOrderSummaryRequest, setCustomerOrderSummaryRequest] = useState<WebsocketRequest | null>(null);
  const handleUpdatedCustomerOrderRequest = useCallback((request: WebsocketRequest | null) => {
    if (request === null) {
      setCustomerOrderSummaryRequest(null);
      return;
    }
    setCustomerOrderSummaryRequest({
      ...request,
      name: CUSTOMER_ORDER_SUMMARY,
      tag: 'CUSTOMER_RECENT_ORDER_SUMMARIES',
    });
  }, []);

  const { data: customerOrderSummariesObs } = useSubscription<ICustomerOrderSummary>(
    enableMonitoringCustomerOrderSummaryColumns ? customerOrderSummaryRequest : null
  );

  const enrichWithCustomerOrderSummaryFieldsPipe = useMemo(
    () =>
      pipe(
        wsStitchWith({
          getPrimaryTypeKey: customerOrder => customerOrder.OrderID,
          getSecondaryTypeKey: customerOrderSummary => customerOrderSummary.OrderID,
          secondarySource: customerOrderSummariesObs,
          stitch: (customerOrder, customerOrderSummary) => {
            customerOrder.enrichOrderWithCustomerSummary?.(customerOrderSummary);
            return customerOrder;
          },
        })
      ),
    [customerOrderSummariesObs]
  ) satisfies CompositePipeFunction<CustomerOrder>;

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: CUSTOMER_ORDER,
      sort_by: '-SubmitTime',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, CustomerOrder>({
    request,
    onInnerRequestChanged: handleUpdatedCustomerOrderRequest,
    rowID: CustomerOrder.rowID satisfies keyof CustomerOrder,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    pipe: enrichWithCustomerOrderSummaryFieldsPipe,
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    contextMenuOptions: { getCustomContextMenuItems: CustomerOrdersMenu.getCustomContextMenuItems },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'CustomerOrders',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    if (persistedBlotterTable.state != null) {
      onCloneTab?.();
    }
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Toggle
              label="Summary Columns"
              size={FormControlSizes.Small}
              tooltip="Include Summary Columns in the table"
              checked={enableMonitoringCustomerOrderSummaryColumns}
              onChange={setEnableMonitoringCustomerOrderSummaryColumns}
            />

            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.SubmitTime} />
      <BlotterTable {...blotterTable} />
      {CustomerOrdersMenu.dialogs}
    </>
  );
}
